import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

export default function Merging() {
    return (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
                Merging Products
            </h2>
            <div className="grid gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Understanding Merging</CardTitle>
                        <CardDescription>
                            How product merging works in Optifi
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Merging allows you to combine multiple product
                            variants or similar products into a single
                            optimization group. This helps maintain consistent
                            pricing across related items and collect more data
                            for products with lower visitor numbers.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">When to use merging:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>
                                    Products with multiple variants (size,
                                    color, etc.)
                                </li>
                                <li>
                                    Similar products with shared characteristics
                                </li>
                                <li>
                                    Products that should maintain price
                                    relationships
                                </li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Auto-Merge Feature</CardTitle>
                        <CardDescription>
                            Automatic variant grouping
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Optifi's auto-merge feature automatically groups
                            different variants under the same product heading,
                            making it easier to manage related products.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Auto-merge benefits:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>Automatic detection of product variants</li>
                                <li>Consistent pricing across all variants</li>
                                <li>Simplified product management</li>
                                <li>Reduced manual grouping effort</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>How to Merge Products</CardTitle>
                        <CardDescription>
                            Step-by-step merging process
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            To merge products, select the items you want to
                            combine and use the merge option in the product
                            actions menu.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Merging process:</p>
                            <ol className="mt-2 list-decimal pl-4 text-sm space-y-2">
                                <li>Select the products you want to merge</li>
                                <li>
                                    Click the merge option in the actions menu
                                </li>
                                <li>Confirm the merge operation</li>
                                <li>Review the merged product group</li>
                            </ol>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Unmerging Products</CardTitle>
                        <CardDescription>
                            Separating merged product groups
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            You can unmerge products at any time to separate
                            them back into individual items or create new
                            groups.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Unmerging options:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>
                                    Unmerge individual products from a group
                                </li>
                                <li>
                                    Split a group into multiple smaller groups
                                </li>
                                <li>
                                    Completely dissolve a group into individual
                                    products
                                </li>
                                <li>
                                    Preserve optimization settings during
                                    unmerge
                                </li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Managing Merged Products</CardTitle>
                        <CardDescription>
                            Working with merged product groups
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            After merging, you can manage the product group as a
                            single unit for optimization purposes while
                            maintaining individual product details.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Key features:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>Group-level optimization settings</li>
                                <li>Individual product overrides</li>
                                <li>Bulk price adjustments</li>
                                <li>Group performance analytics</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
